/* Stock-aware collection browsing. Only variant.available sizes can match.
 * Load all server-filtered pages before filtering, retaining Shopify sorting,
 * prices, market context and native product-card markup. No catalog writes. */
class AcuCollectionFilter extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.keys = ['acu_size', 'acu_type', 'acu_family', 'acu_gender'];
    this.status = this.querySelector('[data-acu-status]');
    this.addEventListener('change', (event) => {
      if (!this.keys.includes(event.target.name)) return;
      const params = new URLSearchParams(location.search);
      event.target.value ? params.set(event.target.name, event.target.value) : params.delete(event.target.name);
      params.delete('page');
      params.delete('acu_page');
      this.renderPage(params.toString(), null, true, false);
    });
    this.querySelector('[data-acu-clear]').addEventListener('click', (event) => {
      event.preventDefault();
      const params = new URLSearchParams(location.search);
      this.keys.forEach((key) => params.delete(key));
      params.delete('acu_page');
      params.delete('page');
      this.renderPage(params.toString(), null, true, false);
    });
    const start = () => {
      const params = new URLSearchParams(location.search);
      this.syncInputs(params);
      this.renderPage(params.toString(), null, false, false);
    };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start, { once: true }) : start();
  }

  disconnectedCallback() { this.clearColorwayPreview(); this.controller?.abort(); this.pageCache?.clear(); }

  hasSelection(params) { return this.keys.some((key) => params.get(key)); }

  syncInputs(params) {
    this.querySelectorAll('input,select').forEach((input) => {
      const value = params.get(input.name) || '';
      if (input.type === 'radio') input.checked = input.value === value;
      else input.value = value;
    });
  }

  async fetchPage(params, page, signal) {
    const query = new URLSearchParams(params);
    [...this.keys, 'acu_page'].forEach((key) => query.delete(key));
    query.set('section_id', this.dataset.sectionId);
    query.set('page', String(page));
    // Reuse raw responses only within this mounted page for fifteen seconds.
    // Keep native filters, sort, market, locale, pathname and page in the key.
    query.sort();
    const context = [location.pathname, window.Shopify?.country || '',
      window.Shopify?.currency?.active || '', document.documentElement.lang || ''];
    const cacheKey = JSON.stringify(context) + '?' + query.toString();
    this.pageCache ||= new Map();
    const cached = this.pageCache.get(cacheKey);
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (cached && Date.now() - cached.at < 15000) {
      const doc = new DOMParser().parseFromString(cached.html, 'text/html');
      if (doc.querySelector('#ProductGridContainer')) return { html: cached.html, doc };
    }
    this.pageCache.delete(cacheKey);
    const response = await fetch(`${location.pathname}?${query}`, {
      signal: AbortSignal.any([signal, AbortSignal.timeout(30000)]),
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Collection request failed');
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.querySelector('#ProductGridContainer')) throw new Error('Incomplete collection response');
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    this.pageCache.set(cacheKey, { html, at: Date.now() });
    if (this.pageCache.size > 32) this.pageCache.delete(this.pageCache.keys().next().value);
    return { html, doc };
  }

  async renderPage(searchParams, event, updateURL = true, preserveSelection = true) {
    this.clearColorwayPreview();
    const params = new URLSearchParams(searchParams);
    // A retired category link opens All footwear; it must not produce empty results.
    if (params.get('acu_type') === 'runner-inspired') {
      params.delete('acu_type');
      params.delete('acu_page');
      params.delete('page');
      const normalized = new URLSearchParams(location.search);
      normalized.delete('acu_type');
      normalized.delete('acu_page');
      normalized.delete('page');
      history.replaceState({ searchParams: normalized.toString() }, '', location.pathname + (normalized.size ? '?' + normalized : '') + location.hash);
    }
    // Dawn forms know native facets; retain the size/style choice on native changes.
    if (preserveSelection && updateURL) {
      const current = new URLSearchParams(location.search);
      this.keys.forEach((key) => { if (!params.has(key) && current.get(key)) params.set(key, current.get(key)); });
      params.delete('acu_page');
    }
    // Keep preview/localization parameters when a native filter form omits them.
    const current = new URLSearchParams(location.search);
    ['preview_theme_id', 'view'].forEach((key) => { if (!params.has(key) && current.has(key)) params.set(key, current.get(key)); });
    if (!this.querySelector('[name="acu_gender"]')) params.delete('acu_gender');
    if (params.has('page') && !params.has('acu_page')) params.set('acu_page', params.get('page'));
    params.delete('page');
    const search = params.toString();
    FacetFiltersForm.searchParamsPrev = search;
    if (updateURL) history.pushState({ searchParams: search }, '', `${location.pathname}${search ? '?' + search : ''}${location.hash}`);
    this.syncInputs(params);
    this.controller?.abort();
    const controller = this.controller = new AbortController();
    const gridContainer = document.getElementById('ProductGridContainer');
    gridContainer.setAttribute('aria-busy', 'true');
    delete this.status.dataset.error;
    this.status.textContent = params.get('acu_size') ? 'Checking in-stock pairs in your size…' : 'Updating your selection…';
    try {
      const first = await this.fetchPage(params, 1, controller.signal);
      const pages = Number(first.doc.querySelector('#ProductGridContainer').dataset.acuPages || 1);
      const results = [first];
      {
        // Bound concurrent requests, never truncate the number of result pages.
        for (let page = 2; page <= pages; page += 3) {
          const batch = [];
          for (let offset = 0; offset < 3 && page + offset <= pages; offset++) batch.push(this.fetchPage(params, page + offset, controller.signal));
          results.push(...await Promise.all(batch));
        }
      }
      if (controller.signal.aborted || this.controller !== controller) return;
      FacetFiltersForm.renderFilters(first.html, event);
      FacetFiltersForm.renderProductCount(first.html);
      const firstGrid = first.doc.querySelector('#ProductGridContainer');
      gridContainer.innerHTML = firstGrid.innerHTML;
      gridContainer.dataset.acuPages = firstGrid.dataset.acuPages;
      gridContainer.dataset.acuPage = firstGrid.dataset.acuPage;
      {
        const seen = new Set();
        const cards = results.flatMap(({ doc }) => [...doc.querySelectorAll('#product-grid > [data-acu-product-id]')]).filter((card) => {
          if (seen.has(card.dataset.acuProductId)) return false;
          seen.add(card.dataset.acuProductId);
          return true;
        });
        this.cards = cards.filter((card) => {
          if (params.get('acu_type') && card.dataset.acuType !== params.get('acu_type') && !JSON.parse(card.dataset.acuTypes || '[]').includes(params.get('acu_type'))) return false;
          if (params.get('acu_family') && card.dataset.acuFamily !== params.get('acu_family')) return false;
          const gender = params.get('acu_gender');
          if (gender && card.dataset.acuGender !== gender && !(gender !== 'unisex' && card.dataset.acuGender === 'unisex')) return false;
          const size = params.get('acu_size');
          if (!size) return true;
          const variant = JSON.parse(card.dataset.acuStock).find((item) => item.size.replace(/^EU\s*/i, '').trim() === size);
          if (!variant) return false;
          card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
            const url = new URL(link.href, location.origin);
            url.searchParams.set('variant', variant.id);
            link.href = url.pathname + url.search + url.hash;
          });
          return true;
        });
        const sort = params.get('sort_by') || this.dataset.defaultSort;
        if (sort === 'manual' && !params.get('acu_family')) this.cards = this.curatedOrder(this.cards);
        this.addAvailability(this.cards, params);
        this.addColorways(this.cards, cards, params);
        this.filteredParams = params;
        this.paintPage(Number(params.get('acu_page') || 1));
      }
      gridContainer.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
      gridContainer.querySelector('.collection')?.classList.remove('loading');
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      window.Shopify?.PaymentButton?.init();
      this.dataset.ready = 'true';
      document.dispatchEvent(new CustomEvent('acu:collection-ready', { detail: { count: this.cards?.length ?? null } }));
    } catch (error) {
      if (controller.signal.aborted || this.controller !== controller) return;
      this.cards = null;
      // Never leave unfiltered cards looking like size-matched results after a failure.
      this.status.dataset.error = 'true';
      this.status.textContent = 'We couldn’t check this selection. Please retry or reset size & style.';
      const collection = gridContainer.querySelector('.collection');
      if (collection) {
        collection.replaceChildren();
        const notice = document.createElement('p');
        notice.className = 'acu-filter-results__notice';
        notice.textContent = 'Your selection has not been verified.';
        const retry = document.createElement('button');
        retry.type = 'button'; retry.className = 'acu-filter-retry'; retry.textContent = 'Retry';
        retry.addEventListener('click', () => this.renderPage(params.toString(), null, false, false));
        // Keep Dawn’s section lookup available for a subsequent native filter change.
        const marker = document.createElement('div'); marker.id = 'product-grid'; marker.dataset.id = this.dataset.sectionId;
        collection.append(notice, retry, marker); collection.classList.remove('loading');
      }
      console.warn('Acupuncture collection selection could not be verified.', error.name);
    } finally {
      if (this.controller === controller) {
        gridContainer.removeAttribute('aria-busy');
        document.querySelectorAll('.product-count.loading').forEach((element) => element.classList.remove('loading'));
        document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach((element) => element.classList.add('hidden'));
      }
    }
  }

  curatedOrder(cards) {
    // Stable merchandising sequence, only for Featured. Explicit customer sorts win.
    const priority = ['Beefer','Ripple','NYUE','Exosonic','Fat Lace','Curb','Ginger Lion','NYU Vulc','Distort','Reverb','UGNG','Tank','Riff','Echo','Bass','Feedback','Gingypock','Monolith','ACU Ava','NYU Slide'];
    const groups = new Map();
    cards.forEach(card => { const family = card.dataset.acuFamily; if (!groups.has(family)) groups.set(family, []); groups.get(family).push(card); });
    const families = [...priority.filter(f => groups.has(f)), ...[...groups.keys()].filter(f => !priority.includes(f))];
    const result = [];
    while (result.length < cards.length) families.forEach(f => { if (groups.get(f).length) result.push(groups.get(f).shift()); });
    return result;
  }

  addAvailability(cards, params) {
    const size = params.get('acu_size');
    for (const card of cards) {
      card.querySelectorAll('.acu-availability').forEach(note => note.remove());
      if (!size) continue;
      const variant = JSON.parse(card.dataset.acuStock).find(item => item.size.replace(/^EU\s*/i, '').trim() === size);
      if (variant?.limited !== true) continue;
      const info = card.querySelector('.card > .card__content .card__information');
      if (!info) continue;
      const note = document.createElement('p'); note.className = 'acu-availability';
      const marker = document.createElement('span'); marker.className = 'acu-availability__marker'; marker.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span'); label.textContent = 'Limited availability';
      const context = document.createElement('span'); context.className = 'visually-hidden'; context.textContent = ' — one pair remaining in EU ' + size + '.';
      label.append(context); note.append(marker, label); info.append(note);
    }
  }

  requestColorwayPreview(card, sibling, swatch) {
    this.clearColorwayPreview();
    const media = card.querySelector('.card__media .media');
    const primary = media?.querySelector('img');
    if (!media || !primary || !sibling.dataset.acuImage) return;
    let source;
    try {
      source = new URL(sibling.dataset.acuImage, location.origin);
      source.searchParams.set('width', String(Math.min(1200, Math.max(720, Math.ceil(primary.clientWidth * (window.devicePixelRatio || 1))))));
    } catch (_) { return; }
    const image = document.createElement('img');
    image.className = 'acu-colorway-preview';
    if (sibling.querySelector('.card__media img')?.classList.contains('acu-image--whiteback')) image.classList.add('acu-image--whiteback');
    image.alt = ''; image.setAttribute('aria-hidden', 'true'); image.decoding = 'async';
    const originalStyle = getComputedStyle(primary);
    image.style.objectFit = originalStyle.objectFit;
    image.style.objectPosition = originalStyle.objectPosition;
    const preview = this.colorwayPreview = { card, swatch, image };
    image.onload = () => {
      if (this.colorwayPreview !== preview || !card.isConnected || !image.naturalWidth) return;
      media.append(image);
      card.classList.add('acu-colorway-previewing');
    };
    image.onerror = () => { if (this.colorwayPreview === preview) this.clearColorwayPreview(swatch); };
    // Keep the original photo visible until the requested image has loaded.
    image.src = source.href;
  }

  clearColorwayPreview(swatch) {
    const preview = this.colorwayPreview;
    if (!preview || (swatch && preview.swatch !== swatch)) return;
    this.colorwayPreview = null;
    preview.image.onload = null; preview.image.onerror = null;
    preview.image.remove();
    preview.card.classList.remove('acu-colorway-previewing');
  }

  addColorways(cards, catalog, params) {
    const styles = new Map();
    catalog.forEach(card => { const style = card.dataset.acuStyle; if (!styles.has(style)) styles.set(style, []); styles.get(style).push(card); });
    for (const card of cards) {
      card.querySelectorAll('.acu-colorways').forEach(el => el.remove());
      const siblings = styles.get(card.dataset.acuStyle) || [];
      if (siblings.length < 2) continue;
      const info = card.querySelector('.card > .card__content .card__information');
      if (!info) continue;
      const group = document.createElement('div'); group.className = 'acu-colorways'; group.setAttribute('role', 'group'); group.setAttribute('aria-label', `${card.dataset.acuStyle} colorways`);
      const caption = document.createElement('p'); caption.className = 'acu-colorways__caption'; caption.textContent = `${siblings.length} colors`;
      const list = document.createElement('div'); list.className = 'acu-colorways__list';
      for (const sibling of siblings) {
        const stock = JSON.parse(sibling.dataset.acuStock);
        const size = params.get('acu_size');
        const variant = size ? stock.find(v => v.size.replace(/^EU\s*/i, '').trim() === size) : null;
        const gender = params.get('acu_gender');
        const genderMatches = !gender || sibling.dataset.acuGender === gender || (gender !== 'unisex' && sibling.dataset.acuGender === 'unisex');
        const disabled = !stock.length || (size && !variant) || !genderMatches;
        const swatch = document.createElement(disabled ? 'span' : 'a');
        const color = sibling.dataset.acuColor || sibling.dataset.acuStyle;
        swatch.className = 'acu-colorways__swatch';
        if (disabled) { swatch.classList.add('acu-colorways__swatch--unavailable'); swatch.setAttribute('aria-label', `${color}${size && !variant ? ` — not available in EU ${size}` : ' — unavailable in this selection'}`); swatch.setAttribute('role', 'img'); }
        else { const url = new URL(sibling.dataset.acuUrl, location.origin); if (variant) url.searchParams.set('variant', variant.id); swatch.href = url.pathname + url.search; swatch.setAttribute('aria-label', `${sibling.dataset.acuStyle}, ${color}${size ? `, EU ${size} in stock` : ''}`); }
        swatch.title = swatch.getAttribute('aria-label');
        if (sibling.dataset.acuProductId === card.dataset.acuProductId) { swatch.classList.add('acu-colorways__swatch--current'); swatch.setAttribute('aria-current', 'true'); }
        const image = document.createElement('img'); image.src = sibling.dataset.acuImage; image.alt = ''; image.width = 64; image.height = 64; image.loading = 'lazy';
        if (sibling.querySelector('.card__media img')?.classList.contains('acu-image--whiteback')) image.classList.add('acu-image--whiteback');
        swatch.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'touch' && window.matchMedia('(hover: hover)').matches) this.requestColorwayPreview(card, sibling, swatch);
        });
        swatch.addEventListener('pointerleave', () => this.clearColorwayPreview(swatch));
        swatch.addEventListener('focus', () => { if (swatch.matches(':focus-visible')) this.requestColorwayPreview(card, sibling, swatch); });
        swatch.addEventListener('blur', () => this.clearColorwayPreview(swatch));
        swatch.append(image); list.append(swatch);
      }
      group.append(caption, list); info.append(group); card.classList.add('acu-has-colorways');
    }
  }

  paintPage(requested) {
    this.clearColorwayPreview();
    const container = document.getElementById('ProductGridContainer');
    let grid = container.querySelector('#product-grid');
    // A native filter can yield an empty server result; still show a useful state.
    if (!grid || grid.tagName !== 'UL') {
      const collection = container.querySelector('.collection');
      collection.replaceChildren();
      grid = document.createElement('ul'); grid.id = 'product-grid'; grid.dataset.id = this.dataset.sectionId;
      grid.className = 'grid product-grid grid--2-col-tablet-down grid--4-col-desktop';
      collection.append(grid);
    }
    container.querySelectorAll('.pagination-wrapper,.acu-filter-results__pagination').forEach((element) => element.remove());
    const count = this.cards.length;
    const pageSize = Number(this.dataset.pageSize);
    const totalPages = Math.max(1, Math.ceil(count / pageSize));
    const page = Math.max(1, Math.min(totalPages, Number.isFinite(requested) ? requested : 1));
    grid.replaceChildren(...this.cards.slice((page - 1) * pageSize, page * pageSize));
    grid.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
    if (!count) {
      const empty = document.createElement('li'); empty.className = 'acu-filter-results__empty';
      const heading = document.createElement('h2'); heading.textContent = 'No pairs match this selection';
      const message = document.createElement('p'); message.textContent = 'Try another EU size, footwear type or family.';
      empty.append(heading, message); grid.append(empty);
    }
    if (totalPages > 1) {
      const nav = document.createElement('nav'); nav.className = 'acu-filter-results__pagination'; nav.setAttribute('aria-label', 'Collection pagination');
      const label = document.createElement('span'); label.textContent = `${page} / ${totalPages}`;
      const button = (text, target, disabled) => {
        const control = document.createElement('button'); control.type = 'button'; control.textContent = text; control.disabled = disabled;
        control.addEventListener('click', () => {
          this.filteredParams.set('acu_page', String(target));
          const search = this.filteredParams.toString(); FacetFiltersForm.searchParamsPrev = search;
          history.pushState({ searchParams: search }, '', `${location.pathname}?${search}`);
          this.paintPage(target);
          // Move focus out of the removed pagination before announcing the new page.
          this.status.tabIndex = -1; this.status.focus({ preventScroll: true });
          this.status.scrollIntoView({ block: 'start', behavior: 'instant' });
        });
        return control;
      };
      nav.append(button('Previous', page - 1, page === 1), label, button('Next', page + 1, page === totalPages));
      grid.after(nav);
    }
    const size = this.filteredParams.get('acu_size');
    const countText = `${count} ${count === 1 ? 'product' : 'products'}${size ? ` available in EU ${size}` : ''}`;
    document.querySelectorAll('#ProductCount,#ProductCountDesktop,.mobile-facets__count').forEach((element) => { element.textContent = countText; });
    this.status.textContent = `${countText}.${totalPages > 1 ? ` Page ${page} of ${totalPages}.` : ''}`;
  }
}
if (!customElements.get('acu-collection-filter')) customElements.define('acu-collection-filter', AcuCollectionFilter);
